import { updateTask } from '@commenda/api';
import { Button, Icons, useToggle } from '@commenda/kakashi';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { documentName, getPrintableDirectorName } from '@commenda/utils';
import { TSrPageTask } from '@commenda/types/src/service-request/types';
import { IncorporationRequestStatus, LegacyDocumentType, TaskStatus } from '@prisma/client';
import { ConfirmationModal } from '../../../../molecules/modals/ConfirmationModal';
import { useState } from 'react';
import { KeyedMutator } from 'swr';
import { useNotification } from '../../../../hooks/useNotification';
import { WorkflowProcessDrawer } from '../../../common/workflow-process-drawer';
import {
  areDocumentsPresent,
  isDocumentPresent,
  isDocumentPresentForAnyStakeholder,
} from '../../../utils';
import { ReviewAndSignDocViewer } from './ReviewAndSignDocViewer';

type DocSignModalProps = {
  open: boolean;
  onClose: () => void;
  task: TSrPageTask;
  serviceRequestId: string;
  onMarkTaskAsComplete: () => Promise<void>;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
};

export function DocSignModal({
  open,
  onClose,
  task,
  onMarkTaskAsComplete,
  serviceRequest,
  mutate,
}: DocSignModalProps) {
  const { setNotification } = useNotification();

  const confirmationModalVisibility = useToggle();

  const corporationStakeholders =
    serviceRequest?.corporation?.corporationHasDirector || [];

  // Common documents for the corporation
  const USCCorpShareholdingDocuments = [
    {
      unsigned: LegacyDocumentType.UnsignedBoardResolution,
      signed: LegacyDocumentType.BoardResolution,
    },
  ];

  // Document types that need to be created for each director
  const USCCorpShareholdingIndividualDirectorDocuments = [
    {
      unsigned: LegacyDocumentType.UnsignedSharePurchaseAndSaleAgreement,
      signed: LegacyDocumentType.SharePurchaseAndSaleAgreement,
    },
    {
      unsigned: LegacyDocumentType.UnsignedShareCertificate,
      signed: LegacyDocumentType.ShareCertificate,
    },
  ];

  const isTaskCompleted =
    areDocumentsPresent({
      documentTypes: USCCorpShareholdingDocuments.map((each) => each.signed),
      serviceRequest,
    }) &&
    USCCorpShareholdingIndividualDirectorDocuments.every((documentType) =>
      isDocumentPresentForAnyStakeholder({
        corporationStakeholders,
        documentType: documentType.signed,
      })
    );

  const corporationSteps = USCCorpShareholdingDocuments.map(
    (eachDocumentType) => ({
      heading: documentName(eachDocumentType.signed),
      key: eachDocumentType.signed,
      description: '',
      status: isDocumentPresent({
        documentType: eachDocumentType.signed,
        serviceRequest,
      })
        ? IncorporationRequestStatus.Completed
        : IncorporationRequestStatus.NotStarted,
      children: (
        <ReviewAndSignDocViewer
          serviceRequestTaskId={task.id}
          serviceRequest={serviceRequest}
          mutate={mutate}
          reviewDocumentType={eachDocumentType.unsigned}
          documentType={eachDocumentType.signed}
          onUploadSuccess={async () => {
            if (task.status === TaskStatus.TO_DO) {
              const res = await updateTask({
                id: task.id,
                data: { status: TaskStatus.IN_PROGRESS },
              });
              if (!res.ok) {
                setNotification({
                  show: true,
                  valid: false,
                  title: 'Error',
                  message: 'Failed to update the task status.',
                });
              }
            }
          }}
        />
      ),
    })
  );

  const directorSteps = USCCorpShareholdingIndividualDirectorDocuments.flatMap(
    (documentType) =>
      corporationStakeholders.map((each) => {
        const isDocAvailable = each?.documents?.some(
          (e) => e?.documentType === documentType.signed
        );
        return {
          heading: `${documentName(documentType.signed)} - ${getPrintableDirectorName(
            {
              directorFirstName: each.director.directorFirstName,
              directorMiddleName: each.director.directorMiddleName,
              directorLastName: each.director.directorLastName,
            }
          )}`,
          key: `${documentType.signed}_${each?.director.id}`,
          description: '',
          status: isDocAvailable
            ? IncorporationRequestStatus.Completed
            : IncorporationRequestStatus.NotStarted,
          children: (
            <ReviewAndSignDocViewer
              serviceRequestTaskId={task.id}
              serviceRequest={serviceRequest}
              mutate={mutate}
              reviewDocumentType={documentType.unsigned}
              documentType={documentType.signed}
              directorId={each?.director?.id}
              onUploadSuccess={async () => {
                if (task.status === TaskStatus.TO_DO) {
                  const res = await updateTask({
                    id: task.id,
                    data: { status: TaskStatus.IN_PROGRESS },
                  });
                  if (!res.ok) {
                    setNotification({
                      show: true,
                      valid: false,
                      title: 'Error',
                      message: 'Failed to update the task status.',
                    });
                  }
                }
              }}
            />
          ),
        };
      })
  );

  const steps = [...corporationSteps, ...directorSteps];

  const [activeParentStepId, setActiveParentStepId] = useState<string>(
    steps[0].key
  );

  return (
    <WorkflowProcessDrawer
      isShown={open}
      activeParentStepId={activeParentStepId}
      setActiveParentStepId={setActiveParentStepId}
      onClose={onClose}
      hideHeader
      header={
        <div className="w-full flex justify-between mr-4 items-center">
          <h3 className="txt-large-medium text-text-secondary ">
            Review and sign documents
          </h3>
          <div className="flex items-center gap-2">
            {task?.status !== TaskStatus.COMPLETED && (
              <Button
                variant="primary"
                disabled={!isTaskCompleted}
                size="sm"
                className="txt-small-medium"
                onClick={confirmationModalVisibility.show}
              >
                <Icons.Check className="w-4 h-4" /> Mark task as complete
              </Button>
            )}
          </div>
          <ConfirmationModal
            isOpen={confirmationModalVisibility.visible}
            onClose={confirmationModalVisibility.hide}
            onConfirm={() => {
              onMarkTaskAsComplete();
              confirmationModalVisibility.hide();
            }}
            title="Mark task as complete."
            message="Are you sure you want to mark this task as complete? This action cannot be undone."
            hideCancelButton
          />
        </div>
      }
      steps={steps}
    />
  );
}
